// =============================================================================
// PaymentCapture — single OR mixed/split-tender payment entry
// =============================================================================
// Default is a single payment (the rich PaymentFields: method tiles, quick-fills,
// COD, "after this payment" preview). Pick the "Mixed payment" tile to settle one
// amount across several methods (e.g. part cash + part Juice) — each line becomes
// its own payment, posted atomically via the backend's /payments/orders/:id/batch
// endpoint. The parent owns the `lines` array and submits it; gate the submit
// button with `paymentsValid(lines, cap)`.

import { useState } from 'react';
import type { PaymentMethod } from '@kalachand/shared';
import { Money } from './Money';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { cn } from '../lib/cn';
import {
  PaymentFields,
  METHOD_TILES,
  emptyPayment,
  paymentValid,
  paymentNeedsRef,
  type PaymentDraft,
} from './PaymentFields';

const MAX_LINES = 6;

/** Sum of the line amounts, rounded to cents. */
export function paymentsTotal(lines: PaymentDraft[]): number {
  return Math.round(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;
}

/** Every line valid (positive amount + required reference) and the total settled
 *  is positive and within `cap` (the order total or remaining balance). */
export function paymentsValid(lines: PaymentDraft[], cap: number): boolean {
  if (lines.length === 0 || !lines.every(paymentValid)) return false;
  const sum = paymentsTotal(lines);
  return sum > 0 && sum <= cap + 0.01;
}

export function PaymentCapture({
  lines,
  onChange,
  total,
  userName,
  shopName,
  canRecordCheque = false,
  depositOption,
}: {
  lines: PaymentDraft[];
  onChange: (lines: PaymentDraft[]) => void;
  /** The amount being settled — order total (deposit step) or remaining balance. */
  total: number;
  userName?: string;
  shopName?: string;
  canRecordCheque?: boolean;
  depositOption?: { label: string; value: number };
}): JSX.Element {
  const [split, setSplit] = useState(lines.length > 1);
  const methods = canRecordCheque ? METHOD_TILES : METHOD_TILES.filter((m) => m.key !== 'cheque');

  const entered = paymentsTotal(lines);
  const remaining = Math.round((total - entered) * 100) / 100;

  const setLine = (i: number, patch: Partial<PaymentDraft>): void =>
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = (): void =>
    onChange([...lines, emptyPayment(remaining > 0 ? String(remaining) : '')]);
  const removeLine = (i: number): void => onChange(lines.filter((_, idx) => idx !== i));

  // ── Single mode: the familiar rich field + a "Mixed payment" tile ────────
  if (!split) {
    return (
      <PaymentFields
        draft={lines[0] ?? emptyPayment('')}
        onChange={(d) => onChange([d])}
        balance={total}
        userName={userName}
        shopName={shopName}
        canRecordCheque={canRecordCheque}
        depositOption={depositOption}
        onMixed={() => setSplit(true)}
      />
    );
  }

  // ── Split mode: compact rows that must sum to the amount being settled ───
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium">
          Mixed payment
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            setSplit(false);
            onChange([lines[0] ?? emptyPayment('')]);
          }}
        >
          Use a single method
        </Button>
      </div>

      <div className="space-y-2">
        {lines.map((line, i) => (
          <div key={i} className="rounded-md border border-rule p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={line.method}
                onChange={(e) => setLine(i, { method: e.target.value as PaymentMethod })}
                className="kc-input flex-1 text-sm"
              >
                {methods.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1.5 rounded-md border border-rule bg-surface px-2 py-1.5 focus-within:border-accent w-36">
                <span className="text-ink-3 text-sm">Rs</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.amount}
                  onChange={(e) => setLine(i, { amount: e.target.value })}
                  className="w-full min-w-0 kc-mono kc-tnum bg-transparent text-ink focus:outline-none text-right"
                  placeholder="0"
                />
              </div>
              {lines.length > 1 && (
                <IconButton
                  aria-label="Remove payment line"
                  variant="danger"
                  size="sm"
                  onClick={() => removeLine(i)}
                >
                  ×
                </IconButton>
              )}
            </div>
            {paymentNeedsRef(line.method) && (
              <input
                value={line.reference}
                onChange={(e) => setLine(i, { reference: e.target.value })}
                placeholder="Reference / receipt # (required)"
                className="kc-input w-full text-sm"
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button variant="tertiary" size="xs" onClick={addLine} disabled={lines.length >= MAX_LINES}>
          + Add another method
        </Button>
        <div className={cn('text-xs', remaining < -0.001 ? 'text-negative' : 'text-ink-3')}>
          Entered <Money value={String(entered)} className="text-ink font-medium" />
          {remaining > 0.001 ? (
            <>
              {' '}
              · <Money value={String(remaining)} /> left of {total.toLocaleString()}
            </>
          ) : remaining < -0.001 ? (
            ' · over the amount due'
          ) : (
            ' · covers it'
          )}
        </div>
      </div>
    </div>
  );
}
